/**
 * Open the domain record behind a Task Manager work-queue row.
 * Same behavior as Denial Management: click the item to open details.
 */
import {
  openDenialDetail,
  openClaimDetail,
  openEobDetail,
  openArPostingPage,
  openContractDetail,
  openRcmServicePage,
} from '../crossAppNav/rcmCrossAppNav.js';
import { resolveOpenableLinkedId } from './workQueueLink.js';

function sourceKey(item) {
  return String(
    item?.source_service ||
      item?.external_service ||
      item?.target_service ||
      item?.item_type ||
      item?.type ||
      ''
  )
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function domainId(item, keys) {
  return resolveOpenableLinkedId(item, keys, {
    allowDomainId: true,
    skipLinkedClaim: true,
  });
}

/**
 * @param {object} item
 * @param {string} [organizationId]
 * @returns {boolean} true when a navigation was attempted
 */
export function openWorkQueueRecord(item, organizationId) {
  if (!item || typeof item !== 'object') return false;
  const src = sourceKey(item);

  if (src.includes('denial')) {
    const id = resolveOpenableLinkedId(item, ['claim_id', 'claimId', 'linked_claim_id']);
    if (id) openDenialDetail(id, organizationId);
    else openRcmServicePage(3154, '/work-queue', organizationId, 'Denial Work Queue');
    return true;
  }
  if (src.includes('claim')) {
    const id = resolveOpenableLinkedId(item, ['claim_id', 'claimId', 'linked_claim_id']);
    if (id) openClaimDetail(id, organizationId);
    else openRcmServicePage(3153, '/work-queue', organizationId, 'Claims Work Queue');
    return true;
  }
  if (src.includes('remittance') || src.includes('eob') || src === 'explanation_of_benefits') {
    const id = domainId(item, ['eob_id', 'era_id']);
    if (id) openEobDetail(id, organizationId);
    else openRcmServicePage(3157, '/eobs', organizationId, 'Remittance Advice');
    return true;
  }
  if (src.includes('ar_posting') || src.includes('payment')) {
    const id = domainId(item, ['posting_id', 'external_id', 'domain_id']);
    if (id) {
      openArPostingPage(
        `/payments?posting_id=${encodeURIComponent(id)}`,
        organizationId,
        'Payment posting'
      );
    } else {
      openRcmServicePage(3155, '/work-queue', organizationId, 'AR Posting Work Queue');
    }
    return true;
  }
  if (src.includes('contract')) {
    const id = domainId(item, ['contract_id', 'external_id', 'domain_id']);
    if (id) openContractDetail(id, organizationId);
    else openRcmServicePage(3156, '/contracts', organizationId, 'Contract Management');
    return true;
  }
  if (src.includes('prior_auth') || src.includes('authorization')) {
    const id = domainId(item, ['request_id', 'authorization_id', 'external_id', 'domain_id']);
    const path = id ? `/preauth-requests/${encodeURIComponent(id)}` : '/work-queue';
    openRcmServicePage(3151, path, organizationId, 'Prior Authorization');
    return true;
  }
  if (src.includes('insurance') || src.includes('eligib')) {
    const id = domainId(item, ['request_id', 'eligibility_id', 'external_id', 'domain_id']);
    const path = id ? `/eligibility/${encodeURIComponent(id)}` : '/work-queue';
    openRcmServicePage(3150, path, organizationId, 'Insurance Verification');
    return true;
  }
  if (src.includes('charge')) {
    openRcmServicePage(3152, '/work-queue', organizationId, 'Charge Capture');
    return true;
  }
  if (src.includes('compliance')) {
    openRcmServicePage(3158, '/work-queue', organizationId, 'RCM Compliance');
    return true;
  }
  if (src.includes('patient')) {
    openRcmServicePage(3110, '/work-queue', organizationId, 'Patient Billing');
    return true;
  }

  const fallback = resolveOpenableLinkedId(item, ['claim_id', 'claimId', 'linked_claim_id']);
  if (fallback) {
    openDenialDetail(fallback, organizationId);
    return true;
  }
  return false;
}
